import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';

import { Forecast } from '@futrli-sdk/predict';
import { ConsolidatedForecastUpdateRequest } from '@futrli-sdk/consolidations';

import { forceLocalDate } from '../../../../utils/dates';
import * as styles from './scenario-date-picker.scss';

interface Props {
  forecastFromDate?: string | null;
  includeUnpaidInvoices?: boolean;
  updateForecast:
    | ((
        forecast: Partial<Forecast> | Partial<ConsolidatedForecastUpdateRequest>,
        shouldRefetchReport?: boolean
      ) => Promise<void>)
    | undefined;
  windowFrom: string;
  windowTill: string;
}

export const ScenarioDatePicker = ({
  forecastFromDate: forecastFromDateProp,
  includeUnpaidInvoices,
  updateForecast,
  windowFrom,
  windowTill
}: Props) => {
  const intl = useIntl();
  const maxDate = format(forceLocalDate(windowTill), 'yyyy-MM-dd');
  const minDate = format(forceLocalDate(windowFrom), 'yyyy-MM-dd');
  const [forecastFromDate, setForecastFromDate] = useState(
    forecastFromDateProp ? format(forceLocalDate(forecastFromDateProp), 'yyyy-MM-dd') : ''
  );

  // Sync with context changes
  useEffect(() => {
    if (forecastFromDateProp) {
      const newIso = format(forceLocalDate(forecastFromDateProp), 'yyyy-MM-dd');
      if (newIso !== forecastFromDate) {
        setForecastFromDate(newIso);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastFromDateProp]);

  const handleForecastDateChange = (e: DateChangeEvent) => {
    const raw = e.target.value.rawValue;
    const iso = format(forceLocalDate(raw || ''), 'yyyy-MM-dd');

    if (iso < minDate || iso > maxDate) {
      return;
    }

    setForecastFromDate(iso);

    updateForecast?.({ forecast_from_date: iso }, true);
  };

  if (includeUnpaidInvoices) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto' }}>
      <div style={{ marginRight: 10 }} className={styles.scenarioPeriodTitle}>
        <FormattedMessage id="predict.show_forecast_data_from" defaultMessage="Show forecast data from" />
        {':'}
      </div>

      <DateInput
        maxDate={maxDate}
        minDate={minDate}
        value={forecastFromDate}
        onChange={handleForecastDateChange}
        aria-label={intl.formatMessage({
          id: 'predict.show_forecast_data_from',
          defaultMessage: 'Show forecast data from'
        })}
        key={forecastFromDate}
      />
    </div>
  );
};
